import React, { useState, useEffect } from 'react';
import { X, Loader, Send, Plus, Trash2, FileText, ScanLine, Mail, AlertTriangle, Check } from 'lucide-react';

// ── Return a Mail Monitor to AP ───────────────────────────────────────────────
// At the end of a monitor AP wants its Mail Monitor Data Sheet back: one line per day
// (the item caught, or NO MAIL) and a photo of anything that was held. The lines start
// from the drivers' daily check-ins; a parcel line can be filled by reading the label off
// the driver's photo. Preview writes onto AP's own sheet; Send emails it with the chosen
// photos to To/Cc/Bcc and (ticked by default) closes the monitor.
// Backed by mail-stopper-admin-sb: monitorReturnPrep / monitorSheetPdf / readLabel /
// recipientsSave / monitorReturnSend.

const URL = '/.netlify/functions/mail-stopper-admin-sb';
const api = (action, payload = {}) =>
  fetch(URL, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action, ...payload }) }).then((r) => r.json());

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const NO_MAIL = 'NO MAIL';
const fmtDate = (iso) => (iso ? new Date(iso + 'T00:00').toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' }) : '');
const isNoMail = (r) => (r.recipient || '').trim().toUpperCase() === NO_MAIL;

const labelStyle = { fontSize: '.72rem', color: 'var(--hp-gold)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '.08em', display: 'block', marginBottom: 5 };
const cell = { padding: '4px 4px', borderTop: '1px solid var(--hp-border)', verticalAlign: 'middle' };
const cellInput = { color: 'var(--hp-text)', background: 'var(--hp-surface)', width: '100%', padding: '5px 7px', fontSize: '.82rem' };
const th = { padding: '6px 4px', textAlign: 'left', fontSize: '.64rem', textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--hp-dim)', fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap' };

// Mirrors defaultReturnMessage in mail-stopper-admin-sb (keep in sync) so the draft
// follows the lines until the message is edited by hand.
const niceDate = (iso) => (iso ? new Date(iso + 'T00:00').toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : '');
function buildMessage(m, rows) {
  const caught = rows.filter((r) => r.date && !isNoMail(r));
  const items = caught.map((r) => `- ${niceDate(r.date)}: ${[r.recipient, r.sender && `from ${r.sender}`, r.origin && `(${r.origin})`, r.description].filter(Boolean).join(' ')}`).join('\n');
  return [
    `Hi ${m.greet || 'there'},`,
    '',
    `Please find attached the completed Mail Monitor Data Sheet for AP Ref ${m.apRef} — ${m.address}, monitoring period ${m.from} to ${m.to}.`,
    '',
    caught.length
      ? `${caught.length === 1 ? 'One item was' : `${caught.length} items were`} received (photo attached):\n${items}\n\nNo mail was received on the other days recorded.`
      : 'No mail was received during the monitoring period.',
    '',
    'Regards,',
    'PH Bouveng Pty Ltd',
  ].join('\n');
}

function b64ToBlobUrl(b64, type) {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return window.URL.createObjectURL(new Blob([bytes], { type }));
}

export default function MailMonitorReturn({ stopper, notify, onClose, onSent }) {
  const [prep, setPrep] = useState(null);
  const [rows, setRows] = useState([]);
  const [photoSel, setPhotoSel] = useState({});   // path → bool
  const [book, setBook] = useState([]);
  const [picked, setPicked] = useState({});       // email → 'to' | 'cc' | 'bcc'
  const [newName, setNewName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [messageEdited, setMessageEdited] = useState(false);
  const [close, setClose] = useState(true);
  const [replyOn, setReplyOn] = useState(false);  // send into the requester's own thread
  const [msgId, setMsgId] = useState('');
  const [quoteOn, setQuoteOn] = useState(true);
  const [quoteText, setQuoteText] = useState('');
  const [reading, setReading] = useState('');     // photo path being read
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);   // { url, pages, usedTemplate }
  const [sending, setSending] = useState(false);
  const [reviewing, setReviewing] = useState(false); // the last look before Send

  useEffect(() => {
    (async () => {
      const r = await api('monitorReturnPrep', { id: stopper.id });
      if (!r.success) { notify?.(r.error || 'Could not load the check-ins', 'error'); onClose(); return; }
      setPrep(r);
      setRows(r.rows.map((x) => ({ date: x.date || '', recipient: x.recipient || '', sender: x.sender || '', origin: x.origin || '', description: x.description || '' })));
      const recvDates = new Set(r.rows.filter((x) => !isNoMail(x)).map((x) => x.date));
      const sel = {};
      r.photos.forEach((p) => { sel[p.path] = recvDates.has(p.date); });
      setPhotoSel(sel);
      setBook(r.recipients || []);
      const pk = {};
      (r.suggested || []).forEach((s) => { if (s.pick) pk[s.email] = s.pick; });
      setPicked(pk);
      const rp = r.reply || {};
      setReplyOn(!!rp.available);
      setMsgId(rp.messageId || '');
      setQuoteText(rp.quote || '');
      setSubject(rp.available ? rp.subject : r.subject);
      setMessage(r.message);
    })();
  }, [stopper.id, notify, onClose]);

  useEffect(() => {
    if (prep && prep.msg && !messageEdited) setMessage(buildMessage(prep.msg, rows));
  }, [prep, rows, messageEdited]);

  useEffect(() => () => { if (preview && preview.url) window.URL.revokeObjectURL(preview.url); }, [preview]);

  const setCell = (i, k, v) => { setRows((rs) => rs.map((r, j) => (j === i ? { ...r, [k]: v } : r))); setPreview(null); };
  const removeRow = (i) => { setRows((rs) => rs.filter((_, j) => j !== i)); setPreview(null); };
  const addRow = (r = {}) => {
    setRows((rs) => [...rs, { date: '', recipient: '', sender: '', origin: '', description: '', ...r }]
      .sort((a, b) => (a.date || '9999').localeCompare(b.date || '9999')));
    setPreview(null);
  };
  const missedLeft = prep ? prep.missed.filter((d) => !rows.some((r) => r.date === d)) : [];

  const readLabel = async (photo, rowIndex) => {
    setReading(photo.path);
    const r = await api('readLabel', { id: stopper.id, path: photo.path });
    setReading('');
    if (!r.success) { notify?.(r.error || 'Could not read the label', 'error'); return; }
    const l = r.label;
    setRows((rs) => rs.map((x, j) => (j === rowIndex ? {
      ...x,
      recipient: l.recipient || x.recipient,
      sender: l.sender || x.sender,
      origin: l.origin || x.origin,
      description: l.description || x.description,
    } : x)));
    setPreview(null);
    notify?.('Read the label — check it against the photo', 'success');
  };

  const doPreview = async () => {
    setPreviewing(true);
    const r = await api('monitorSheetPdf', { id: stopper.id, rows });
    setPreviewing(false);
    if (!r.success) { notify?.(r.error || 'Could not build the sheet', 'error'); return false; }
    if (preview && preview.url) window.URL.revokeObjectURL(preview.url);
    setPreview({ url: b64ToBlobUrl(r.pdf, 'application/pdf'), pages: r.pages, usedTemplate: r.usedTemplate, filename: r.filename });
    return true;
  };

  // Build the sheet from the lines as they are now, then show it with the email for a last look.
  const review = async () => {
    if (!to.length) { notify?.('Put at least one recipient in To', 'error'); return; }
    if (await doPreview()) {
      setReviewing(true);
      const ov = document.getElementById('mm-return-overlay');
      if (ov) ov.scrollTop = 0;
    }
  };

  // Address book: the shared AP book plus this request's own suggestions.
  const people = book.map((p) => ({ ...p }));
  (prep?.suggested || []).forEach((s) => { if (!people.some((p) => p.email === s.email)) people.push({ name: s.name, email: s.email, why: s.why }); });
  people.forEach((p) => { const s = (prep?.suggested || []).find((x) => x.email === p.email); if (s) p.why = s.why; });

  const saveBook = async (list) => {
    setBook(list);
    const r = await api('recipientsSave', { recipients: list });
    if (r.success) setBook(r.recipients); else notify?.(r.error || 'Failed to save the recipient', 'error');
  };
  const addRecipient = () => {
    const email = newEmail.trim().toLowerCase();
    if (!EMAIL_RE.test(email)) { notify?.('Enter a valid email address', 'error'); return; }
    if (!book.some((r) => r.email === email)) saveBook([...book, { name: newName.trim(), email }]);
    setPicked((p) => ({ ...p, [email]: 'to' }));
    setNewName(''); setNewEmail('');
  };

  const slot = (s) => Object.keys(picked).filter((e) => picked[e] === s);
  const to = slot('to'), cc = slot('cc'), bcc = slot('bcc');
  const total = to.length + cc.length + bcc.length;
  const photoPaths = Object.keys(photoSel).filter((p) => photoSel[p]);
  const blankItems = rows.filter((r) => r.date && !isNoMail(r) && !r.recipient.trim()).length;
  const undated = rows.filter((r) => !r.date && (r.recipient || r.sender || r.description)).length;

  const send = async () => {
    if (!to.length) { notify?.('Put at least one recipient in To', 'error'); return; }
    if (blankItems && !window.confirm(`${blankItems} item line${blankItems > 1 ? 's have' : ' has'} no recipient name. Send anyway?`)) return;
    setSending(true);
    const r = await api('monitorReturnSend', { id: stopper.id, rows, to, cc, bcc, subject: subject.trim(), message: message.trim(), photoPaths, close,
      reply: { on: replyOn, messageId: msgId.trim(), quote: quoteOn && !!quoteText.trim(), quoteText } });
    setSending(false);
    if (!r.success) { notify?.(r.error || 'Failed to send', 'error'); return; }
    const miss = r.photosSkipped ? ` — ${r.photosSkipped} photo${r.photosSkipped > 1 ? 's' : ''} too large to attach` : '';
    notify?.(`Sheet for ${stopper.ap_ref} sent to ${r.to.join(', ')}${r.photos ? ` with ${r.photos} photo${r.photos > 1 ? 's' : ''}` : ''}${r.asReply ? (r.threaded ? ' as a reply in the thread' : ' as RE: (no Message-ID)') : ''}${r.closed ? ' — monitor closed' : ''}${miss}${r.stampError ? ` (sent, but not recorded: ${r.stampError})` : ''}`,
      miss || r.stampError ? 'error' : 'success');
    onSent();
  };

  const nameFor = (email) => { const p = people.find((x) => x.email === email); return p && p.name ? `${p.name} <${email}>` : email; };
  const quoteShown = replyOn && quoteOn && quoteText.trim();
  const threadNote = !replyOn ? 'New email (not in a thread)'
    : /@/.test(msgId) ? `Reply in ${(prep?.reply?.from || '').split('<')[0].replace(/"/g, '').trim() || 'their'}'s thread`
      : 'RE: their subject — no Message-ID, so threading is up to their mail app';
  const sheetFrame = preview && (
    <iframe title="Completed Mail Monitor Data Sheet" src={`${preview.url}#view=FitH`}
      style={{ width: '100%', height: 'min(620px, 75vh)', border: '1px solid var(--hp-border)', borderRadius: 8, background: '#fff' }} />
  );

  const photosByDate = (d) => (prep?.photos || []).filter((p) => p.date === d);

  return (
    <div id="mm-return-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.55)', zIndex: 1200, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: '24px 12px' }}
      onClick={(e) => e.target === e.currentTarget && !sending && onClose()}>
      <div className="card" style={{ width: 'min(1000px, 100%)', padding: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid var(--hp-border)' }}>
          <h3 style={{ margin: 0, fontSize: '1.05rem', display: 'flex', alignItems: 'center', gap: 8 }}>
            <Mail size={17} style={{ color: 'var(--hp-gold)' }} /> Return monitor sheet · AP {stopper.ap_ref}
          </h3>
          <button className="btn btn-ghost btn-sm" onClick={onClose} disabled={sending}><X size={16} /></button>
        </div>

        {!prep ? (
          <div style={{ padding: 24, color: 'var(--hp-dim)' }}><Loader size={15} /> Loading the check-ins…</div>
        ) : reviewing ? (
          <div style={{ padding: 20 }}>
            <div style={{ fontSize: '.85rem', marginBottom: 14, color: 'var(--hp-dim)' }}>
              This is exactly what will go. Nothing is sent until you press <b>{close ? 'Stop monitor & send' : 'Send'}</b>.
            </div>
            <label style={labelStyle}>The email</label>
            <div style={{ border: '1px solid var(--hp-border)', borderRadius: 8, padding: 14, marginBottom: 18, fontSize: '.86rem', lineHeight: 1.55 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '70px 1fr', gap: '3px 10px', marginBottom: 10 }}>
                <span style={{ color: 'var(--hp-dim)' }}>From</span><span>PH Bouveng Pty Ltd · replies to enquiriesphb@gmail.com</span>
                <span style={{ color: 'var(--hp-dim)' }}>To</span><span>{to.map(nameFor).join(', ')}</span>
                {cc.length > 0 && <><span style={{ color: 'var(--hp-dim)' }}>Cc</span><span>{cc.map(nameFor).join(', ')}</span></>}
                <span style={{ color: 'var(--hp-dim)' }}>Bcc</span><span>{[...bcc.map(nameFor), 'paul@hollypaul.com'].join(', ')}</span>
                <span style={{ color: 'var(--hp-dim)' }}>Subject</span><span style={{ fontWeight: 700 }}>{subject.trim() || '(default subject)'}</span>
                <span style={{ color: 'var(--hp-dim)' }}>Thread</span><span>{threadNote}</span>
              </div>
              <div style={{ whiteSpace: 'pre-wrap', borderTop: '1px solid var(--hp-border)', paddingTop: 10 }}>{message.trim()}</div>
              {quoteShown && (
                <div style={{ marginTop: 12, color: 'var(--hp-dim)', fontSize: '.8rem' }}>
                  {(prep.reply && prep.reply.from) || 'They'} wrote:
                  <div style={{ whiteSpace: 'pre-wrap', borderLeft: '2px solid var(--hp-border)', paddingLeft: 10, marginTop: 4 }}>{quoteText.trim()}</div>
                </div>
              )}
              <div style={{ borderTop: '1px solid var(--hp-border)', marginTop: 12, paddingTop: 10, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><FileText size={14} style={{ color: 'var(--hp-gold)' }} /> {preview ? preview.filename : 'Data sheet'}</span>
                {photoPaths.map((pp) => { const ph = prep.photos.find((x) => x.path === pp); return ph ? <img key={pp} src={ph.url} alt="attached parcel" title={`Parcel photo ${fmtDate(ph.date)}`} style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 6, border: '1px solid var(--hp-border)' }} /> : null; })}
                {!photoPaths.length && <span style={{ fontSize: '.78rem', color: 'var(--hp-dim)' }}>no photos</span>}
              </div>
            </div>

            <label style={labelStyle}>The sheet — {preview ? `${preview.pages} page${preview.pages > 1 ? 's' : ''}${preview.usedTemplate ? ', on AP\'s own form' : ''}` : ''}</label>
            {sheetFrame}
            {preview && <a href={preview.url} target="_blank" rel="noreferrer" style={{ fontSize: '.78rem', display: 'inline-block', marginTop: 6 }}>Open the sheet in a new tab</a>}
            {close && (
              <div style={{ fontSize: '.8rem', marginTop: 12, color: 'var(--hp-dim)' }}>Sending also <b>stops the monitor</b> — it leaves {stopper.driver_login || 'the driver'}'s portal.</div>
            )}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap', marginTop: 16 }}>
              <button className="btn btn-secondary" onClick={() => setReviewing(false)} disabled={sending}>← Back to edit</button>
              <button className="btn btn-primary" onClick={send} disabled={sending}>
                {sending ? <><Loader size={14} /> Sending…</> : <><Send size={14} /> {close ? 'Stop monitor & send' : 'Send'} to {total} recipient{total === 1 ? '' : 's'}</>}
              </button>
            </div>
          </div>
        ) : (
          <div style={{ padding: 20 }}>
            {/* 1 — the sheet's lines */}
            <label style={labelStyle}>Sheet lines — one per day</label>
            <div style={{ fontSize: '.78rem', color: 'var(--hp-dim)', marginBottom: 8, lineHeight: 1.5 }}>
              Built from the drivers' daily check-ins. AP's rule: on a day with nothing, write <b>NO MAIL</b> in the recipient column.
              {!prep.hasTemplate && <> No AP sheet is stored on this request, so an equivalent sheet will be drawn.</>}
            </div>
            <div style={{ overflowX: 'auto', border: '1px solid var(--hp-border)', borderRadius: 8 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 760 }}>
                <thead>
                  <tr>
                    <th style={{ ...th, width: 138, paddingLeft: 8 }}>Date received</th>
                    <th style={th}>Name of mail recipient</th>
                    <th style={th}>Sender</th>
                    <th style={{ ...th, width: 90 }}>Origin</th>
                    <th style={th}>Description</th>
                    <th style={{ ...th, width: 30 }} />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => {
                    const item = r.date && !isNoMail(r);
                    const photos = item ? photosByDate(r.date) : [];
                    return (
                      <React.Fragment key={i}>
                        <tr style={{ background: item ? 'rgba(220,38,38,.06)' : 'transparent' }}>
                          <td style={{ ...cell, paddingLeft: 8 }}>
                            <input type="date" className="input" style={cellInput} value={r.date} onChange={(e) => setCell(i, 'date', e.target.value)} />
                          </td>
                          <td style={cell}>
                            <input className="input" style={{ ...cellInput, fontWeight: isNoMail(r) ? 700 : 400 }} value={r.recipient} placeholder={item ? 'Name on the label' : ''}
                              onChange={(e) => setCell(i, 'recipient', e.target.value)} />
                          </td>
                          <td style={cell}><input className="input" style={cellInput} value={r.sender} onChange={(e) => setCell(i, 'sender', e.target.value)} disabled={isNoMail(r)} /></td>
                          <td style={cell}><input className="input" style={cellInput} value={r.origin} placeholder={item ? 'VIC' : ''} onChange={(e) => setCell(i, 'origin', e.target.value)} disabled={isNoMail(r)} /></td>
                          <td style={cell}><input className="input" style={cellInput} value={r.description} placeholder={item ? 'Small parcel' : ''} onChange={(e) => setCell(i, 'description', e.target.value)} disabled={isNoMail(r)} /></td>
                          <td style={{ ...cell, textAlign: 'center' }}>
                            <button className="btn btn-ghost btn-sm" style={{ padding: 4 }} title="Remove line" onClick={() => removeRow(i)}><Trash2 size={13} /></button>
                          </td>
                        </tr>
                        {photos.length > 0 && (
                          <tr style={{ background: 'rgba(220,38,38,.06)' }}>
                            <td colSpan={6} style={{ padding: '2px 8px 8px' }}>
                              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                                {photos.map((p) => (
                                  <div key={p.path} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <a href={p.url} target="_blank" rel="noreferrer"><img src={p.url} alt="parcel" style={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 6, border: '1px solid var(--hp-border)' }} /></a>
                                    <button className="btn btn-secondary btn-sm" disabled={!!reading} onClick={() => readLabel(p, i)}>
                                      {reading === p.path ? <><Loader size={12} /> Reading…</> : <><ScanLine size={12} /> Fill from label</>}
                                    </button>
                                  </div>
                                ))}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                  {rows.length === 0 && (
                    <tr><td colSpan={6} style={{ ...cell, padding: 14, color: 'var(--hp-dim)' }}>No check-ins recorded. Add the days below.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 8 }}>
              <button className="btn btn-ghost btn-sm" onClick={() => addRow()}><Plus size={13} /> Add line</button>
              <span style={{ fontSize: '.76rem', color: 'var(--hp-dim)' }}>{rows.length} line{rows.length === 1 ? '' : 's'}{rows.length > 15 ? ` · ${Math.ceil(rows.length / 15)} pages (15 per AP sheet)` : ''}</span>
            </div>
            {missedLeft.length > 0 && (
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginTop: 10, background: 'rgba(217,119,6,.08)', border: '1px solid #d97706', borderRadius: 6, padding: '8px 10px', fontSize: '.8rem' }}>
                <AlertTriangle size={14} style={{ color: '#d97706', flexShrink: 0, marginTop: 2 }} />
                <div style={{ flex: 1 }}>
                  No driver answer on <b>{missedLeft.map(fmtDate).join(', ')}</b>. Nothing is written for those days unless you add them.
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
                    {missedLeft.map((d) => (
                      <button key={d} className="btn btn-ghost btn-sm" style={{ fontSize: '.74rem' }} onClick={() => addRow({ date: d, recipient: NO_MAIL })}>
                        <Plus size={11} /> {fmtDate(d)} as NO MAIL
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', margin: '12px 0 18px' }}>
              <button className="btn btn-secondary btn-sm" onClick={doPreview} disabled={previewing || !rows.length}>
                {previewing ? <><Loader size={13} /> Building…</> : <><FileText size={13} /> Preview sheet</>}
              </button>
              {preview && (
                <a className="btn btn-ghost btn-sm" href={preview.url} target="_blank" rel="noreferrer" download={preview.filename}>
                  <Check size={13} style={{ color: '#16a34a' }} /> Open {preview.filename} ({preview.pages} page{preview.pages > 1 ? 's' : ''}{preview.usedTemplate ? ', on AP\'s sheet' : ''})
                </a>
              )}
              {preview && <button className="btn btn-ghost btn-sm" onClick={() => setPreview(null)}>Hide</button>}
              {undated > 0 && <span style={{ fontSize: '.76rem', color: '#d97706' }}>{undated} line{undated > 1 ? 's have' : ' has'} no date</span>}
            </div>
            {preview && <div style={{ marginBottom: 18 }}>{sheetFrame}</div>}

            {/* 2 — photos */}
            {prep.photos.length > 0 && (
              <div style={{ marginBottom: 18 }}>
                <label style={labelStyle}>Attach photos</label>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  {prep.photos.map((p) => (
                    <label key={p.path} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, cursor: 'pointer', fontSize: '.72rem', color: 'var(--hp-dim)' }}>
                      <img src={p.url} alt="parcel" style={{ width: 84, height: 84, objectFit: 'cover', borderRadius: 8, border: `2px solid ${photoSel[p.path] ? 'var(--hp-gold)' : 'var(--hp-border)'}`, opacity: photoSel[p.path] ? 1 : 0.5 }} />
                      <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <input type="checkbox" checked={!!photoSel[p.path]} onChange={(e) => setPhotoSel((s) => ({ ...s, [p.path]: e.target.checked }))} />
                        {fmtDate(p.date)} · {p.driver}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* 3 — recipients */}
            <label style={labelStyle}>Recipients</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10 }}>
              {people.map((r) => (
                <div key={r.email} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.87rem', cursor: 'pointer', flex: 1, minWidth: 0, flexWrap: 'wrap' }}>
                    <input type="checkbox" checked={!!picked[r.email]}
                      onChange={(e) => setPicked((p) => { const n = { ...p }; if (e.target.checked) n[r.email] = 'to'; else delete n[r.email]; return n; })} />
                    <span style={{ fontWeight: 700 }}>{r.name || r.email}</span>
                    {r.name && <span style={{ color: 'var(--hp-dim)', fontSize: '.8rem', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.email}</span>}
                    {r.why && <span style={{ fontSize: '.68rem', color: 'var(--hp-gold)', fontFamily: 'var(--font-mono)' }}>{r.why}</span>}
                  </label>
                  {picked[r.email] && (
                    <select className="input" aria-label={`Send ${r.name || r.email} as`} value={picked[r.email]} style={{ width: 'auto', padding: '3px 6px', fontSize: '.8rem', color: 'var(--hp-text)', background: 'var(--hp-surface)' }}
                      onChange={(e) => setPicked((p) => ({ ...p, [r.email]: e.target.value }))}>
                      <option value="to">To</option><option value="cc">Cc</option><option value="bcc">Bcc</option>
                    </select>
                  )}
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
              <input className="input" style={{ flex: '1 1 150px', color: 'var(--hp-text)', background: 'var(--hp-surface)' }} value={newName} placeholder="Name" onChange={(e) => setNewName(e.target.value)} />
              <input className="input" style={{ flex: '1 1 210px', color: 'var(--hp-text)', background: 'var(--hp-surface)' }} type="email" value={newEmail} placeholder="email@auspost.com.au"
                onChange={(e) => setNewEmail(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addRecipient()} />
              <button className="btn btn-secondary btn-sm" onClick={addRecipient} disabled={!newEmail.trim()}><Plus size={13} /> Add</button>
            </div>
            <div style={{ fontSize: '.75rem', color: 'var(--hp-dim)', marginBottom: 16 }}>Added recipients are saved to the AP address book (shared with Incident Reports and the Issue Register).</div>

            {/* 4 — the email */}
            {prep.reply && prep.reply.available && (() => {
              const rp = prep.reply;
              const okId = /^\s*(message-id:\s*)?<?[^<>\s]+@[^<>\s]+>?\s*$/i.test(msgId);
              return (
                <div style={{ border: '1px solid var(--hp-border)', borderRadius: 8, padding: 12, marginBottom: 14 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.87rem', cursor: 'pointer' }}>
                    <input type="checkbox" checked={replyOn} onChange={(e) => { setReplyOn(e.target.checked); setSubject(e.target.checked ? rp.subject : prep.subject); }} />
                    Reply in the original thread from <b>{rp.from.split('<')[0].replace(/"/g, '').trim() || rp.from}</b>
                  </label>
                  {replyOn && (
                    <div style={{ marginTop: 10, paddingLeft: 24 }}>
                      <label style={labelStyle}>Message-ID of their email</label>
                      <input className="input" style={{ width: '100%', color: 'var(--hp-text)', background: 'var(--hp-surface)', fontFamily: 'var(--font-mono)', fontSize: '.8rem' }}
                        value={msgId} placeholder="<SY4P282MB....@SY4P282MB....AUSP282.PROD.OUTLOOK.COM>" onChange={(e) => setMsgId(e.target.value)} />
                      <div style={{ fontSize: '.74rem', color: msgId && !okId ? '#b91c1c' : 'var(--hp-dim)', marginTop: 4, lineHeight: 1.5 }}>
                        {rp.messageIdSource === 'email' && msgId === rp.messageId
                          ? 'Read from the email when it came in.'
                          : msgId && !okId
                            ? 'That doesn\'t look like a Message-ID — it looks like <something@domain>.'
                            : <>In Gmail, open their email → <b>⋮</b> → <b>Show original</b> → copy the <b>Message-ID</b> line here. {msgId ? '' : 'Without it the email still goes as “RE:” with their subject, which most mail apps thread — just less reliably.'}</>}
                      </div>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.84rem', cursor: 'pointer', marginTop: 10 }}>
                        <input type="checkbox" checked={quoteOn} onChange={(e) => setQuoteOn(e.target.checked)} /> Quote their message below mine
                      </label>
                      {quoteOn && (
                        <>
                          <textarea className="input" style={{ width: '100%', minHeight: 70, marginTop: 6, color: 'var(--hp-dim)', background: 'var(--hp-surface)', fontFamily: 'inherit', fontSize: '.8rem', lineHeight: 1.5, borderLeft: '3px solid var(--hp-border)' }}
                            value={quoteText} onChange={(e) => setQuoteText(e.target.value)}
                            placeholder="Paste their message here, e.g. “Please hand over the monitor sheet for NSW police.”" />
                          {!rp.quote && <div style={{ fontSize: '.72rem', color: 'var(--hp-dim)', marginTop: 2 }}>This request came in before the engine kept the sender's words — paste them if you want them quoted. Left blank, nothing is quoted.</div>}
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })()}
            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>Subject</label>
              <input className="input" style={{ width: '100%', color: 'var(--hp-text)', background: 'var(--hp-surface)' }} value={subject} onChange={(e) => setSubject(e.target.value)} />
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={labelStyle}>Message</label>
              <textarea className="input" style={{ width: '100%', minHeight: 170, color: 'var(--hp-text)', background: 'var(--hp-surface)', fontFamily: 'inherit', lineHeight: 1.5 }} value={message} onChange={(e) => { setMessage(e.target.value); setMessageEdited(true); }} />
              {messageEdited && prep.msg && (
                <button className="btn btn-ghost btn-sm" style={{ marginTop: 4 }} onClick={() => setMessageEdited(false)}>Rewrite from the lines</button>
              )}
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.87rem', marginBottom: 12, cursor: 'pointer' }}>
              <input type="checkbox" checked={close} onChange={(e) => setClose(e.target.checked)} />
              Stop the monitor when sent — it leaves the drivers' portal and shows as <b>completed</b>
            </label>
            <div style={{ fontSize: '.78rem', color: 'var(--hp-dim)', marginBottom: 14, lineHeight: 1.5 }}>
              Attached: <b>AP {stopper.ap_ref} Mail Monitor Data Sheet - completed.pdf</b>{photoPaths.length ? ` and ${photoPaths.length} photo${photoPaths.length > 1 ? 's' : ''}` : ''}.
              Paul gets a blind copy, and replies go to enquiriesphb@gmail.com.
              {prep.sends.length > 0 && <> Already sent {prep.sends.length}×, last {new Date(prep.sends[prep.sends.length - 1].at).toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' })} to {prep.sends[prep.sends.length - 1].to.join(', ')}.</>}
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <button className="btn btn-secondary" onClick={onClose} disabled={sending}>Cancel</button>
              <button className="btn btn-primary" onClick={review} disabled={previewing || !to.length || !rows.length}>
                {previewing ? <><Loader size={14} /> Building the sheet…</> : <><FileText size={14} /> Review before sending</>}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
